import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button, Checkbox, Space,
  Spin
} from 'antd';
import React from 'react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Input from 'common/components/Input';
import { KNOWLEDGE_SOURCES } from 'common/utils/constant';
import mapModifiers from 'common/utils/functions';
import { FormTierValues, FormValues } from 'extends/PackageDetail';

export type TableFieldType = {
  key: string;
  label: string;
  type: string;
};

type CustomServiceType = {
  id: number;
  slug: string;
  text: string;
};

interface PackageTierConfigProps {
  method: UseFormReturn<FormValues>;
  customServicesData?: CustomServiceType[];
  isDisplay?: boolean;
  tiersData?: FormTierValues[];
  isLoading?: boolean;
}

const PackageTierConfig = ({
  method, customServicesData = [], isDisplay, isLoading
}: PackageTierConfigProps) => {
  /* Hook */
  const { t } = useTranslation();

  const formatCustomServicesData = [...customServicesData.map((item) => ({
    key: `customServices.${item.slug}`,
    label: item.text,
    type: 'checkbox'
  })), {
    key: 'customServices.aiAction',
    label: 'AI Action',
    type: 'checkbox'
  }];

  const tableRowHeaders = [
    {
      key: 'general',
      label: t('package.general'),
      children: [{
        key: 'numberOfAgent',
        label: t('package.numberOfAgent'),
        type: 'number',
      }]
    },
    {
      key: 'knowledges',
      label: t('package.knowledgeResources'),
      children: [
        {
          key: `knowledges.${KNOWLEDGE_SOURCES.FILES}`,
          label: t('package.localStorage'),
          type: 'checkbox',
        },
        {
          key: `knowledges.${KNOWLEDGE_SOURCES.GOOGLE_DRIVE}`,
          label: 'Google Drive',
          type: 'checkbox',
        },
        {
          key: `knowledges.${KNOWLEDGE_SOURCES.SHAREPOINT}`,
          label: 'SharePoint',
          type: 'checkbox',
        }, {
          key: `knowledges.${KNOWLEDGE_SOURCES.WEBSITE}`,
          label: 'Website',
          type: 'checkbox',
        },
        {
          key: `knowledges.${KNOWLEDGE_SOURCES.CONFLUENCE}`,
          label: 'Confluence',
          type: 'checkbox',
        },
        {
          key: `knowledges.${KNOWLEDGE_SOURCES.ONEDRIVE}`,
          label: 'One Drive',
          type: 'checkbox',
        },
        {
          key: `knowledges.${KNOWLEDGE_SOURCES.PRIVATE_CLOUD}`,
          label: t('package.privateCloud'),
          type: 'checkbox',
        }
      ]
    },
    {
      key: 'knowledgeLimits',
      label: t('package.knowledgeBaseLimit'),
      children: [
        {
          key: 'knowledgeLimits.numberOfFile',
          label: t('package.numberOfFile'),
          type: 'number',
        },
        {
          key: 'knowledgeLimits.fileSizeLimit',
          label: t('package.fileSizeLimit'),
          type: 'number',
        },
        {
          key: 'knowledgeLimits.totalSizeLimit',
          label: t('package.totalSizeLimit'),
          type: 'number',
        },
        {
          key: 'knowledgeLimits.numberOfWeb',
          label: t('package.numberOfWeb'),
          type: 'number',
        },
      ]
    },
    {
      key: 'customServices',
      label: t('package.customServices'),
      children: formatCustomServicesData
    },
    {
      key: 'endUserSides',
      label: t('package.endUserSides'),
      children: [
        {
          key: 'endUserSides.numberOfSession',
          label: t('package.numberOfSession'),
          type: 'number',
        },
        {
          key: 'endUserSides.chatUpload',
          label: t('package.chatUpload'),
          type: 'checkbox',
        },
        {
          key: 'endUserSides.chatExport',
          label: t('package.chatExport'),
          type: 'checkbox',
        },
        {
          key: 'endUserSides.agentTypeBoth',
          label: t('package.agentTypeBoth'),
          type: 'checkbox',
        },
        {
          key: 'endUserSides.s2t',
          label: 'Speech to text',
          type: 'checkbox',
        },
        {
          key: 'endUserSides.t2s',
          label: 'Text to speech',
          type: 'checkbox',
        }
      ]
    }
  ];

  /* Form handlers */
  const {
    fields, append, remove
  } = useFieldArray({
    control: method.control,
    name: 'tiers'
  });

  // Functions
  const getAllChildrenFields = () => {
    const childrenFields: Array<TableFieldType> = [];
    tableRowHeaders.forEach((row) => {
      row.children.forEach((child) => {
        childrenFields.push(child);
      });
    });
    return childrenFields;
  };

  const watchedTiers = method.watch('tiers');

  const lastTier = watchedTiers?.[watchedTiers.length - 1];

  const getPreviousTierFromCurrent = (currentIdxInWatchedTiers: number) => {
    const previousTierIdx = currentIdxInWatchedTiers - 1;
    if (previousTierIdx < 0) {
      return null;
    }
    return watchedTiers[previousTierIdx];
  };

  const getPreviousTierValue = (key: keyof FormTierValues, tierIdx: number): any => {
    const pTier = getPreviousTierFromCurrent(tierIdx);

    if (key.includes('.')) {
      const keys = key.split('.');
      return pTier?.[keys[0] as keyof FormTierValues]?.
      [keys[1] as keyof typeof pTier[keyof FormTierValues]];
    }

    return pTier?.[key] || 0;
  };

  if (isLoading) {
    return <Spin />;
  }

  return (
    <div>
      <Space direction="vertical" className="u-mt-16 p-packageDetail_tierConfig_wrapper">
        {!isDisplay
          && (
            <Button
              onClick={() => {
                if (method.getValues().isApplyPayg || fields.length < 1) {
                  append(lastTier ?? {
                    numberOfAgent: 0,
                    knowledges: [],
                    knowledgeLimits: {
                      numberOfFile: 0,
                      fileSizeLimit: 0,
                      totalSizeLimit: 0,
                      numberOfWeb: 0,
                    },
                    customServices: {},
                    endUserSides: {
                      numberOfSession: 0,
                      chatUpload: false,
                      chatExport: false,
                      agentTypeBoth: false,
                      s2t: false,
                      t2s: false
                    },
                    price: 0,
                  });
                }
              }}
              className="p-packageDetail_tierConfig_cta"
              disabled={!(method.getValues().isApplyPayg || fields.length < 1)}
            >
              <PlusOutlined />
              {t('package.addAnotherTier')}
            </Button>
          )}

        <div className="p-packageDetail_tierConfig_table_wrapper">
          <table className="p-packageDetail_tierConfig_table">
            <tr>
              <th colSpan={2} className="p-packageDetail_tierConfig_table_firstHeader">Tier</th>
            </tr>

            {tableRowHeaders?.map((row) => (
              <>
                <tr key={row?.key}>
                  <th style={{ minWidth: '200px' }} rowSpan={row.children.length}>{row?.label}</th>
                  <td style={{ minWidth: '400px' }}>{row.children[0]?.label}</td>
                </tr>
                {row?.children.slice(1).map((child) => (
                  <tr key={child?.key}>
                    <td>{child?.label}</td>
                  </tr>
                ))}
              </>
            ))}

            <tr className="p-packageDetail_tierConfig_table_seperator">
              <td />
              <td />
            </tr>

            <tr>
              <th className="p-packageDetail_tierConfig_table_firstHeader">{t('package.cost')}</th>
              <td>{t('package.vnd')}</td>
            </tr>
          </table>
          {fields.map((field, index) => (
            <div className={mapModifiers('p-packageDetail_tierConfig_table', 'empty')} key={field.id}>
              {!isDisplay
                && (
                  <Button
                    type="default"
                    shape="circle"
                    icon={<MinusCircleOutlined size={32} />}
                    className="p-packageDetail_tierConfig_table_remove"
                    onClick={() => remove(index)}
                  />
                )}
              <div className="p-packageDetail_tierConfig_table_row">
                Tier
                {' '}
                {index + 1}
              </div>
              {getAllChildrenFields()?.map((row) => (
                <Controller
                  key={row.key}
                  name={`tiers.${index}.${row.key}` as `tiers.${number}.name` | `tiers.${number}.index`
                    | `tiers.${number}.numberOfAgent` | `tiers.${number}.knowledges` | `tiers.${number}.knowledgeLimits`
                    | `tiers.${number}.customServices` | `tiers.${number}.endUserSides` | `tiers.${number}.price`}
                  render={({ field: colField, fieldState: { error } }) => {
                    const previousTierValue = getPreviousTierValue(`${row.key}` as keyof FormTierValues, index);
                    return (
                      <div className="p-packageDetail_tierConfig_table_row" key={`tiers.${index.toString()}.${row.key}`}>

                        {row.type === 'checkbox' ? (
                          <Checkbox
                            {...colField}
                            onChange={(e) => {
                              if (isDisplay) return;
                              colField.onChange(e.target.checked);
                            }}
                            value={!!colField.value}
                            checked={!!colField.value}
                            className={mapModifiers('p-packageDetail_tierConfig_table_row_checkbox', !!previousTierValue > !!colField.value && 'error')}
                          />
                        ) : (
                          <Input
                            {...colField}
                            disabled={isDisplay}
                            className="p-packageDetail_tierConfig_table_row_input"
                            value={colField.value as number}
                            type="number"
                            error={previousTierValue > colField.value
                              ? error?.message ?? 'Must greater'
                              : undefined}
                            hideErrorMsg
                          />
                        )}
                      </div>
                    );
                  }}
                  control={method.control}
                />
              ))}
              <div className="p-packageDetail_tierConfig_table_seperator" />

              <Controller
                name={`tiers.${index}.price`}
                render={({ field: colField }) => (
                  <div className="p-packageDetail_tierConfig_table_row" key={`tiers.${index.toString()}.price`}>
                    <Input {...colField} disabled={isDisplay} type="number" className="p-packageDetail_tierConfig_table_row_input" />
                  </div>
                )}
                control={method.control}
              />
            </div>
          ))}
        </div>
      </Space>
    </div>
  );
};

export default PackageTierConfig;
